import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import {
	type Application,
	JIRA_SERVICE_DESK,
	JIRA_SOFTWARE,
	JIRA_PRODUCT_DISCOVERY,
	JIRA_CUSTOMER_SERVICE,
	JIRA_CORE,
	getApplicationForProject,
} from './application.tsx';

export const FREE_EDITION = 'FREE_EDITION' as const;
export const STANDARD_EDITION = 'STANDARD_EDITION' as const;
export const PREMIUM_EDITION = 'PREMIUM_EDITION' as const;
export const UNLICENSED = 'UNLICENSED' as const;

export type ApplicationEdition =
	| typeof FREE_EDITION
	| typeof STANDARD_EDITION
	| typeof PREMIUM_EDITION
	| typeof UNLICENSED;

export type ApplicationEditions = {
	readonly core: ApplicationEdition;
	readonly software: ApplicationEdition;
	readonly serviceDesk: ApplicationEdition;
	readonly customerService: ApplicationEdition;
	readonly productDiscovery: ApplicationEdition;
};

// These are the actual edition values in the server.
export const FREE_EDITION_KEY = 'free' as const;
export const STANDARD_EDITION_KEY = 'standard' as const;
export const PREMIUM_EDITION_KEY = 'premium' as const;

export type ApplicationEditionKey =
	| typeof FREE_EDITION_KEY
	| typeof STANDARD_EDITION_KEY
	| typeof PREMIUM_EDITION_KEY;

export const editionKeyToEdition = (editionShort: ApplicationEditionKey): ApplicationEdition => {
	if (editionShort === FREE_EDITION_KEY) {
		return FREE_EDITION;
	}
	if (editionShort === STANDARD_EDITION_KEY) {
		return STANDARD_EDITION;
	}
	if (editionShort === PREMIUM_EDITION_KEY) {
		return PREMIUM_EDITION;
	}

	return UNLICENSED;
};

export const editionToEditionKey = (
	edition: ApplicationEdition,
): ApplicationEditionKey | undefined => {
	switch (edition) {
		case FREE_EDITION:
			return FREE_EDITION_KEY;
		case STANDARD_EDITION:
			return STANDARD_EDITION_KEY;
		case PREMIUM_EDITION:
			return PREMIUM_EDITION_KEY;
		default:
			return undefined;
	}
};

export const populateApplicationEditions = (
	overrides: Partial<ApplicationEditions> = {},
	defaultEdition: ApplicationEdition = UNLICENSED,
): ApplicationEditions => ({
	core: defaultEdition,
	software: defaultEdition,
	serviceDesk: defaultEdition,
	customerService: defaultEdition,
	productDiscovery: defaultEdition,
	...overrides,
});

export const getEdition = (
	application: Application,
	applicationEditions: ApplicationEditions,
): ApplicationEdition => {
	switch (application) {
		case JIRA_CORE:
			return applicationEditions.core;
		case JIRA_SOFTWARE:
			return applicationEditions.software;
		case JIRA_PRODUCT_DISCOVERY:
			return applicationEditions.productDiscovery;
		case JIRA_SERVICE_DESK:
			return applicationEditions.serviceDesk;
		case JIRA_CUSTOMER_SERVICE:
			return applicationEditions.customerService;
		default:
			throw new Error(`Unrecognised application: ${application}`);
	}
};

export const getEditionForProject = (
	projectType: ProjectType,
	applicationEditions: ApplicationEditions,
): ApplicationEdition => {
	const application = getApplicationForProject(projectType);
	return getEdition(application, applicationEditions);
};

export const toEdition = (edition: string): ApplicationEdition => {
	switch (edition) {
		case FREE_EDITION:
			return FREE_EDITION;
		case STANDARD_EDITION:
			return STANDARD_EDITION;
		case PREMIUM_EDITION:
			return PREMIUM_EDITION;
		case UNLICENSED:
		default:
			return UNLICENSED;
	}
};
